import * as THREE from 'three';
import { AXIS_LENGTH } from './constants';

/**
 * AxisVisualizer renders spin axis and magnetic axis indicator lines.
 * Both axes are rendered as THREE.Line objects extending AXIS_LENGTH units
 * from origin in both directions.
 * Spin axis uses cyan; magnetic axis uses magenta for visual distinction.
 */
export class AxisVisualizer {
  public spinAxisLine: THREE.Line;
  public magneticAxisLine: THREE.Line;

  constructor(parent: THREE.Object3D) {
    // Spin axis - cyan color
    const spinGeometry = new THREE.BufferGeometry();
    const spinPositions = new Float32Array(6); // 2 points × 3 components
    spinGeometry.setAttribute('position', new THREE.BufferAttribute(spinPositions, 3));
    const spinMaterial = new THREE.LineBasicMaterial({ color: 0x00ffff });
    this.spinAxisLine = new THREE.Line(spinGeometry, spinMaterial);
    parent.add(this.spinAxisLine);

    // Magnetic axis - magenta color
    const magGeometry = new THREE.BufferGeometry();
    const magPositions = new Float32Array(6); // 2 points × 3 components
    magGeometry.setAttribute('position', new THREE.BufferAttribute(magPositions, 3));
    const magMaterial = new THREE.LineBasicMaterial({ color: 0xff00ff });
    this.magneticAxisLine = new THREE.Line(magGeometry, magMaterial);
    parent.add(this.magneticAxisLine);

    // Initialize with default Y-axis direction
    this.updateSpinAxis(new THREE.Vector3(0, 1, 0));
    this.updateMagneticAxis(new THREE.Vector3(0, 1, 0));
  }

  /**
   * Updates the spin axis line to point along the given direction.
   * Extends AXIS_LENGTH units in both +/- directions from origin.
   */
  updateSpinAxis(direction: THREE.Vector3): void {
    const posAttr = this.spinAxisLine.geometry.getAttribute('position') as THREE.BufferAttribute;
    const dir = direction.clone().normalize();

    // Point 1: -direction * AXIS_LENGTH
    posAttr.setXYZ(0, -dir.x * AXIS_LENGTH, -dir.y * AXIS_LENGTH, -dir.z * AXIS_LENGTH);
    // Point 2: +direction * AXIS_LENGTH
    posAttr.setXYZ(1, dir.x * AXIS_LENGTH, dir.y * AXIS_LENGTH, dir.z * AXIS_LENGTH);

    posAttr.needsUpdate = true;
  }

  /**
   * Updates the magnetic axis line to point along the given direction.
   * Extends AXIS_LENGTH units in both +/- directions from origin.
   */
  updateMagneticAxis(direction: THREE.Vector3): void {
    const posAttr = this.magneticAxisLine.geometry.getAttribute('position') as THREE.BufferAttribute;
    const dir = direction.clone().normalize();

    // Point 1: -direction * AXIS_LENGTH
    posAttr.setXYZ(0, -dir.x * AXIS_LENGTH, -dir.y * AXIS_LENGTH, -dir.z * AXIS_LENGTH);
    // Point 2: +direction * AXIS_LENGTH
    posAttr.setXYZ(1, dir.x * AXIS_LENGTH, dir.y * AXIS_LENGTH, dir.z * AXIS_LENGTH);

    posAttr.needsUpdate = true;
  }
}
